import { z } from 'zod';
import { GMAPS_MAX_PAGES_LIMIT, SCRAPER_AGENT_STATUSES, SCRAPER_MESSAGE_ROLES, SCRAPER_OUTPUT_FORMATS, SCRAPER_PROVIDERS, SCRAPER_SEARCH_BACKENDS, SCRAPER_SEARCH_DEPTHS, SCRAPER_SESSION_STATUSES, SCRAPER_SHARE_STATUSES, SCRAPER_TEMPLATES, } from '../types/scraper.js';
// ----------------------------------------------------------------------------
// Enum schemas
// ----------------------------------------------------------------------------
export const ScraperTemplateSchema = z.enum(SCRAPER_TEMPLATES);
export const ScraperProviderSchema = z.enum(SCRAPER_PROVIDERS);
export const ScraperOutputFormatSchema = z.enum(SCRAPER_OUTPUT_FORMATS);
export const ScraperSearchDepthSchema = z.enum(SCRAPER_SEARCH_DEPTHS);
export const ScraperSearchBackendSchema = z.enum(SCRAPER_SEARCH_BACKENDS);
export const ScraperAgentStatusSchema = z.enum(SCRAPER_AGENT_STATUSES);
export const ScraperSessionStatusSchema = z.enum(SCRAPER_SESSION_STATUSES);
export const ScraperMessageRoleSchema = z.enum(SCRAPER_MESSAGE_ROLES);
export const ScraperShareStatusSchema = z.enum(SCRAPER_SHARE_STATUSES);
// ----------------------------------------------------------------------------
// Agent CRUD
// ----------------------------------------------------------------------------
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const SLUG_RE = /^[a-z0-9-]+$/;
export const ScraperAgentInputSchema = z.object({
    name: z.string().trim().min(1).max(80),
    slug: z.string().trim().min(1).max(80).regex(SLUG_RE),
    description: z.string().max(400).nullable().optional(),
    accentColor: z.string().regex(HEX_COLOR_RE).nullable().optional(),
    template: ScraperTemplateSchema,
    systemPrompt: z
        .string()
        .max(20_000)
        .optional()
        .refine((v) => v === undefined || v === '' || v.length >= 20, {
        message: 'If provided, system prompt must be at least 20 characters. Leave blank to use the template default.',
    }),
    defaultOutputFormat: ScraperOutputFormatSchema,
    defaultTableColumnsHint: z.string().max(400).nullable().optional(),
    provider: ScraperProviderSchema,
    model: z.string().trim().min(1).max(80),
    tavilySearchDepth: ScraperSearchDepthSchema,
    maxToolCallsPerSession: z.number().int().min(3).max(30),
    // Optional so pre-existing clients (and the seeded templates) keep
    // validating; the service resolves the defaults.
    searchBackend: ScraperSearchBackendSchema.optional(),
    gmapsIncludeExtra: z.boolean().optional(),
    gmapsMaxPages: z.number().int().min(1).max(GMAPS_MAX_PAGES_LIMIT).optional(),
});
export const ScraperAgentPatchSchema = ScraperAgentInputSchema.partial().refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' });
// ----------------------------------------------------------------------------
// G Maps Extractor key (admin, Settings → Scraper)
// ----------------------------------------------------------------------------
export const ScraperGmapsKeyUpdateSchema = z.object({
    apiKey: z.string().trim().min(8).max(500),
});
// ----------------------------------------------------------------------------
// Sessions
// ----------------------------------------------------------------------------
export const ScraperSessionStartInputSchema = z.object({
    agentId: z.string().uuid(),
    prompt: z.string().trim().min(1).max(5_000),
});
export const ScraperSessionFollowupInputSchema = z.object({
    content: z.string().trim().min(1).max(5_000),
});
// ----------------------------------------------------------------------------
// Synthesizer output (the JSON the synthesize-step LLM must produce)
// ----------------------------------------------------------------------------
export const ScraperTableColumnSchema = z.object({
    key: z.string().min(1).max(120),
    label: z.string().min(1).max(160),
    type: z.enum(['string', 'number', 'url', 'date', 'boolean']),
});
export const ScraperTableSchema = z.object({
    columns: z.array(ScraperTableColumnSchema).min(1).max(40),
    rows: z.array(z.record(z.string(), z.unknown())).max(500),
});
export const ScraperSourceSchema = z.object({
    title: z.string().min(1).max(400),
    // Restrict to http(s): zod's .url() alone accepts javascript:/data:/file:
    // schemes, which become a DOM-XSS vector when these LLM-synthesized source
    // URLs are rendered as links.
    url: z
        .string()
        .url()
        .max(2_000)
        .refine((u) => /^https?:\/\//i.test(u), 'URL must use http or https'),
    publishedDate: z.string().max(40).nullable().optional(),
});
export const ScraperSynthesizeOutputSchema = z.object({
    intro: z.string().max(2_000).nullable().optional(),
    text: z.string().max(40_000).nullable().optional(),
    table: ScraperTableSchema.nullable().optional(),
    sources: z.array(ScraperSourceSchema).max(50).default([]),
    caveats: z.string().max(2_000).nullable().optional(),
});
// ----------------------------------------------------------------------------
// Sharing
// ----------------------------------------------------------------------------
export const ScraperShareInputSchema = z.object({
    recipientUserIds: z.array(z.string().uuid()).min(1).max(20),
    message: z.string().max(1_000).nullable().optional(),
});
// ----------------------------------------------------------------------------
// Access grants
// ----------------------------------------------------------------------------
export const ScraperGrantAccessSchema = z.object({
    userId: z.string().uuid(),
});
//# sourceMappingURL=scraper.schema.js.map